import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Landmark, Loader2 } from "lucide-react";
import ThemeToggle from "@/components/ThemeToggle";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e, creds) => {
    if (e) e.preventDefault();
    const em = creds?.email || email;
    const pw = creds?.password || password;
    setLoading(true);
    setError("");
    try {
      const u = await login(em, pw);
      toast.success(`Welcome back, ${u.name}`);
      navigate("/");
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="absolute top-4 right-4 z-10">
        <ThemeToggle />
      </div>
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between p-12 relative overflow-hidden border-r border-border">
        <div
          className="absolute inset-0 -z-10"
          style={{
            background:
              "radial-gradient(600px circle at 20% 20%, hsl(160 84% 30% / 0.18), transparent 50%), radial-gradient(500px circle at 90% 80%, hsl(32 95% 44% / 0.12), transparent 45%)",
          }}
        />
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-primary flex items-center justify-center">
            <Landmark className="h-6 w-6 text-primary-foreground" />
          </div>
          <div>
            <div className="font-display font-extrabold text-xl leading-none">Wealthoo 360</div>
            <div className="text-xs text-gold tracking-widest">WEALTH MANAGEMENT CRM</div>
          </div>
        </div>

        <div className="max-w-md">
          <h1 className="font-display text-4xl xl:text-5xl font-extrabold tracking-tight leading-tight">
            One platform for your entire{" "}
            <span className="text-primary">distribution network.</span>
          </h1>
        </div>
        <div className="text-xs text-muted-foreground">© 2026 Wealthoo. Head Office control panel.</div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md animate-fade-up">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="h-10 w-10 rounded-xl bg-primary flex items-center justify-center">
              <Landmark className="h-5 w-5 text-primary-foreground" />
            </div>
            <div className="font-display font-extrabold text-lg">Wealthoo 360</div>
          </div>

          <h2 className="font-display text-2xl font-bold">Sign in</h2>
          <p className="text-sm text-muted-foreground mt-1">Access your role-based dashboard.</p>

          <form onSubmit={submit} className="mt-7 space-y-4">
            <div>
              <label className="text-sm font-medium">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                className="mt-1.5 w-full rounded-lg bg-secondary border border-input px-3.5 py-2.5 text-sm outline-none focus:border-primary transition-colors"
                placeholder="you@company.com"
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                className="mt-1.5 w-full rounded-lg bg-secondary border border-input px-3.5 py-2.5 text-sm outline-none focus:border-primary transition-colors"
                placeholder="••••••••"
                required
              />
            </div>
            {error && (
              <div className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-lg px-3 py-2" data-testid="login-error">
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full rounded-lg bg-primary text-primary-foreground font-semibold py-2.5 flex items-center justify-center gap-2 hover:bg-blue-500 transition-colors disabled:opacity-60"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              Sign In
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
